import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/Common/Auth/AuthContext";
import { useRecentNotifications } from "@/hooks/useRecentNotifications";
import { formatNotificationTimestamp } from "@/utils/notificationHelpers";
import {
  buildNotificationHref,
  canNavigateNotification,
} from "@/utils/notificationCta";
import { trackNotificationClick } from "@/services/api/notifications";
import { withRegionPath } from "@/utils/session";
import styles from "./NotificationDropdown.module.css";
import navbarStyles from "./Navbar.module.css";

function BellIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3a6 6 0 0 0-6 6v3.5c0 .7-.28 1.37-.78 1.86L4 16h16l-1.22-1.64A2.6 2.6 0 0 1 18 12.5V9a6 6 0 0 0-6-6Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M10 19a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function NotificationDropdown() {
  const router = useRouter();
  const { loggedIn } = useAuth();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { items, unreadCount, loading, error, refresh } = useRecentNotifications({
    enabled: true,
    pageSize: 10,
    refreshKey: loggedIn,
  });

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  const badgeCount = items?.length ? unreadCount > 99 ? "99+" : String(unreadCount) : 0;

  const handleNotificationClick = async (item) => {
    const path = buildNotificationHref(item.ctaAction, item.ctaPayload);

    try {
      await trackNotificationClick(item);
    } catch (trackingError) {
      console.warn("[UserTracking] Notification click tracking failed:", trackingError);
    }

    setOpen(false);

    if (path) {
      router.push(withRegionPath(path));
    }
  };

  const isNotificationClickable = (item) =>
    canNavigateNotification(item) ||
    Boolean(item.ctaAction || item.logId || item.campaignId);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={navbarStyles.iconBtn}
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={toggleOpen}
      >
        <BellIcon />
        {(unreadCount && items?.length) > 0 ? (
          <span className={navbarStyles.badge} aria-hidden="true">
            {badgeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.header}>
            <h2 className={styles.headerTitle}>
              Notifications ({unreadCount})
            </h2>
            <Link
              href={withRegionPath("/notifications")}
              className={styles.viewAll}
              onClick={() => setOpen(false)}
            >
              View All
            </Link>
          </div>

          <div className={styles.divider} />

          {loading ? (
            <p className={styles.state}>Loading notifications…</p>
          ) : error ? (
            <p className={`${styles.state} ${styles.stateError}`}>
              Unable to load notifications.
            </p>
          ) : items.length === 0 ? (
            <p className={styles.state}>No notifications yet.</p>
          ) : (
            <ul className={styles.list}>
              {items.map((item) => {
                const clickable = isNotificationClickable(item);
                const ItemTag = clickable ? "button" : "div";

                return (
                  <li
                    key={item.id || `${item.title}-${item.sentOnUtc}`}
                    className={`${styles.item} ${item.isRead ? styles.itemRead : styles.itemUnread
                      } ${clickable ? styles.itemClickable : ""}`}
                  >
                    <ItemTag
                      type={clickable ? "button" : undefined}
                      className={styles.itemButton}
                      onClick={clickable ? () => handleNotificationClick(item) : undefined}
                    >
                      <span
                        className={item.isRead ? styles.dotHidden : styles.dot}
                        aria-hidden="true"
                      />
                      <div className={styles.itemContent}>
                        <p className={styles.itemTitle}>
                          {item.title || "Notification"}
                        </p>
                        {item.description ? (
                          <p className={styles.itemDescription}>{item.description}</p>
                        ) : null}
                        {item.sentOnUtc ? (
                          <p className={styles.itemTime}>
                            {formatNotificationTimestamp(item.sentOnUtc)}
                          </p>
                        ) : null}
                      </div>
                    </ItemTag>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
